<!--
  StockGridSection - Grid display of stock categories with optional countdown

  Props:
  - backgroundColor (String): Background color/gradient of the section (default: 'linear-gradient(to bottom left, #11D2D4, #0C7475)')
  - title (String): Section title
  - titleColor (String): Color of title text (default: 'text-white')
  - titleHighlight (String): Highlighted portion of title
  - titleHighlightColor (String): Color of title highlight (default: 'text-black')
  - image (String): Header image path
  - imageAlt (String): Alt text for header image
  - categories (Array): Array of category objects with title and stocks
  - categoryBackgroundColor (String): Background color of category cards (default: '#FFFFFF')
  - categoryTitleColor (String): Color of category titles (default: 'text-blue-dark')
  - categoryTextColor (String): Color of stock names (default: 'text-black')
  - showCountdown (Boolean): Whether to show countdown card
  - countdown (Object): Countdown object with days, hours, minutes, seconds
  - countdownBackgroundColor (String): Background color of countdown card (default: '#072551')
  - countdownBadgeColor (String): Background color of countdown badge (default: '#1E40AF')
  - backgroundImage (String): Optional background image URL
  - backgroundImageClass (String): CSS classes for background image
-->
<template>
    <section class="relative overflow-hidden py-20" :style="{ background: backgroundColor }">
        <div class="relative container mx-auto px-4 max-w-6xl z-10">
            <div class="flex flex-row justify-between items-center mb-12">
                <h2 class="text-4xl md:text-3xl font-bold text-left" :class="titleColor">
                    <span v-html="renderTitle()"></span>
                </h2>
                <div v-if="image">
                    <img :alt="imageAlt" class="w-2/4 object-contain max-h-[180px] lg:w-auto" :src="$asset(image, 'public')" />
                </div>
            </div>
            <div class="grid md:grid-cols-3 gap-6 mx-auto">
                <!-- Category Cards -->
                <div v-for="(category, index) in categories" :key="index" class="p-6 rounded-2xl" :style="{ backgroundColor: categoryBackgroundColor }">
                    <h3 class="text-lg font-bold mb-4" :class="categoryTitleColor">{{ category.title }}</h3>
                    <ul class="space-y-2 text-sm" :class="categoryTextColor">
                        <li v-for="(stock, sIndex) in category.stocks" :key="sIndex" class="flex gap-3 items-center">
                            <img :alt="stock.name" class="w-[30px]" :src="$asset(stock.image, 'public')" />
                            <p class="font-bold">{{ stock.name }}</p>
                        </li>
                    </ul>
                </div>

                <!-- Countdown Card (Desktop) -->
                <div
                    v-if="showCountdown && countdownPosition === 'desktop'"
                    class="hidden relative overflow-hidden lg:flex text-white p-6 rounded-2xl flex-col justify-center items-center text-center"
                    :style="{ backgroundColor: countdownBackgroundColor }"
                >
                    <div class="relative z-10 text-sm mb-2 font-semibold">{{ countdownLabel }}</div>
                    <div class="relative z-10 text-3xl font-bold mb-2 border-2 rounded-lg p-2" :style="{ backgroundColor: countdownBadgeColor }">
                        {{ formatCountdown() }}
                    </div>
                    <div class="relative z-10 text-sm">{{ countdownSubtext }}</div>
                    <img
                        v-if="countdownBackgroundImage"
                        class="w-full absolute z-0 -left-28 top-10 scale-150"
                        :src="$asset(countdownBackgroundImage, 'public')"
                        alt="Rabatt Icon"
                    />
                </div>

                <!-- Countdown Card (Mobile) -->
                <div
                    v-if="showCountdown && countdownPosition === 'mobile'"
                    class="lg:hidden text-white p-6 rounded-2xl flex flex-col justify-center items-center text-center"
                    :style="{ background: `linear-gradient(to bottom right, ${countdownGradientFrom}, ${countdownGradientTo})` }"
                >
                    <div class="text-sm mb-2 font-semibold">{{ countdownLabel }}</div>
                    <div class="text-3xl font-bold mb-2 border-2 border-white rounded-lg p-2">{{ formatCountdown() }}</div>
                    <div class="text-sm">{{ countdownSubtext }}</div>
                </div>
            </div>
        </div>
        <img v-if="backgroundImage" class="absolute z-0" :class="backgroundImageClass" :src="$asset(backgroundImage, 'public')" alt="" />
    </section>
</template>

<script setup>
const props = defineProps({
    backgroundColor: {
        type: String,
        default: 'linear-gradient(to bottom left, #11D2D4, #0C7475)'
    },
    title: {
        type: String,
        default: ''
    },
    titleColor: {
        type: String,
        default: 'text-white'
    },
    titleHighlight: {
        type: String,
        default: ''
    },
    titleHighlightColor: {
        type: String,
        default: 'text-black'
    },
    image: {
        type: String,
        default: ''
    },
    imageAlt: {
        type: String,
        default: ''
    },
    categories: {
        type: Array,
        default: () => []
    },
    categoryBackgroundColor: {
        type: String,
        default: '#FFFFFF'
    },
    categoryTitleColor: {
        type: String,
        default: 'text-blue-dark'
    },
    categoryTextColor: {
        type: String,
        default: 'text-black'
    },
    showCountdown: {
        type: Boolean,
        default: false
    },
    countdownPosition: {
        type: String,
        default: 'desktop',
        validator: (value) => ['desktop', 'mobile'].includes(value)
    },
    countdown: {
        type: Object,
        default: () => ({ days: 0, hours: 0, minutes: 0, seconds: 0 })
    },
    countdownLabel: {
        type: String,
        default: '20% Rabatt-Code:'
    },
    countdownSubtext: {
        type: String,
        default: 'Einlösbar bis zum 4.3.2026'
    },
    countdownBackgroundColor: {
        type: String,
        default: '#072551'
    },
    countdownBadgeColor: {
        type: String,
        default: '#1E40AF'
    },
    countdownGradientFrom: {
        type: String,
        default: '#1E3A8A'
    },
    countdownGradientTo: {
        type: String,
        default: '#1E40AF'
    },
    countdownBackgroundImage: {
        type: String,
        default: ''
    },
    backgroundImage: {
        type: String,
        default: ''
    },
    backgroundImageClass: {
        type: String,
        default: ''
    }
});

const renderTitle = () => {
    if (!props.titleHighlight) return props.title;

    const parts = props.title.split(props.titleHighlight);

    if (parts.length === 1) return props.title;

    return parts
        .map((part, index) => {
            if (index === 0) {
                return `<span class="${props.titleHighlightColor}">${props.titleHighlight}</span>${part}`;
            }

            return part;
        })
        .join('');
};

const formatCountdown = () => {
    const pad = (num) => String(num).padStart(2, '0');

    return `${pad(props.countdown.days)} : ${pad(props.countdown.hours)} : ${pad(props.countdown.minutes)} : ${pad(props.countdown.seconds)}`;
};
</script>
